import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";

const TYPES = [
  { v: "accident", l: "Accident" },
  { v: "maladie", l: "Maladie" },
  { v: "conge_sans_solde", l: "Congé sans solde" },
  { v: "avance", l: "Avance sur salaire" },
  { v: "prime", l: "Prime" },
  { v: "frais", l: "Remboursement de frais" },
  { v: "indemnite", l: "Indemnité exceptionnelle" },
  { v: "changement_salaire", l: "Changement de salaire" },
  { v: "changement_taux", l: "Changement de taux d'activité" },
  { v: "regularisation", l: "Régularisation d'une période antérieure" },
];

const F = ({ label, children, hint }) => (
  <div>
    <Label className="text-xs text-gray-600">{label}</Label>
    <div className="mt-1">{children}</div>
    {hint && <div className="text-[10px] text-gray-400 mt-0.5">{hint}</div>}
  </div>
);

export default function EvenementDialog({ employe, onClose, onSaved }) {
  const [form, setForm] = useState({
    type: "maladie", taux_incapacite: 100, maintien_salaire_pct: 80,
    delai_attente_jours: 0, indemnite_payee_par: "assureur", professionnel: false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const t = form.type;
  const estAbsence = ["maladie", "accident", "conge_sans_solde"].includes(t);

  const submit = async () => {
    setBusy(true);
    try {
      const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
      await api.post(`/salaires/employes/${employe.id}/evenements`, {
        ...form,
        montant: num(form.montant),
        taux_incapacite: num(form.taux_incapacite),
        maintien_salaire_pct: num(form.maintien_salaire_pct),
        indemnite_journaliere: num(form.indemnite_journaliere),
        delai_attente_jours: num(form.delai_attente_jours),
        taux_activite: num(form.taux_activite),
      });
      toast.success("Événement enregistré — regénérez la fiche du mois concerné");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-xl" data-testid="evenement-dialog">
        <DialogHeader>
          <DialogTitle>Événement salarial — {employe.prenom} {employe.nom}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <F label="Type d'événement">
            <Select value={t} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="evt-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map((x) => <SelectItem key={x.v} value={x.v}>{x.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>

          <div className="grid sm:grid-cols-2 gap-3">
            <F label={estAbsence ? "Date de début" : "Date d'effet"}>
              <Input type="date" value={form.date_debut || ""} onChange={(e) => set("date_debut", e.target.value)} data-testid="evt-date-debut" />
            </F>
            {estAbsence && (
              <F label="Date de fin" hint="Laisser vide si l'absence se poursuit">
                <Input type="date" value={form.date_fin || ""} onChange={(e) => set("date_fin", e.target.value)} data-testid="evt-date-fin" />
              </F>
            )}
          </div>

          {(t === "maladie" || t === "accident") && (
            <div className="grid sm:grid-cols-2 gap-3">
              <F label="Taux d'incapacité (%)">
                <Input type="number" value={form.taux_incapacite ?? 100} onChange={(e) => set("taux_incapacite", e.target.value)} data-testid="evt-incapacite" />
              </F>
              <F label="Maintien du salaire (%)" hint="Part du salaire que l'employeur continue de verser">
                <Input type="number" value={form.maintien_salaire_pct ?? 80} onChange={(e) => set("maintien_salaire_pct", e.target.value)} data-testid="evt-maintien" />
              </F>
              <F label="Indemnité journalière (CHF)">
                <Input type="number" step="0.05" value={form.indemnite_journaliere ?? ""} onChange={(e) => set("indemnite_journaliere", e.target.value)} data-testid="evt-ij" />
              </F>
              <F label="Délai d'attente (jours)">
                <Input type="number" value={form.delai_attente_jours ?? 0} onChange={(e) => set("delai_attente_jours", e.target.value)} data-testid="evt-delai" />
              </F>
              <F label="Indemnité versée par">
                <Select value={form.indemnite_payee_par} onValueChange={(v) => set("indemnite_payee_par", v)}>
                  <SelectTrigger data-testid="evt-paye-par"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="assureur">L'assureur (directement au collaborateur)</SelectItem>
                    <SelectItem value="employeur">L'employeur (avancée sur la paie)</SelectItem>
                  </SelectContent>
                </Select>
              </F>
              {t === "accident" && (
                <label className="flex items-center gap-2 text-sm text-gray-700 mt-5">
                  <input type="checkbox" checked={!!form.professionnel} onChange={(e) => set("professionnel", e.target.checked)}
                         className="rounded border-gray-300 text-[#52a675]" data-testid="evt-professionnel" />
                  Accident professionnel
                </label>
              )}
            </div>
          )}

          {["avance", "prime", "frais", "indemnite", "regularisation", "changement_salaire"].includes(t) && (
            <F label={t === "changement_salaire" ? "Nouveau salaire brut mensuel (CHF)" : "Montant (CHF)"}>
              <Input type="number" step="0.05" value={form.montant ?? ""} onChange={(e) => set("montant", e.target.value)} data-testid="evt-montant" />
            </F>
          )}

          {t === "changement_taux" && (
            <F label="Nouveau taux d'activité (%)">
              <Input type="number" value={form.taux_activite ?? ""} onChange={(e) => set("taux_activite", e.target.value)} data-testid="evt-taux-activite" />
            </F>
          )}

          {["indemnite", "regularisation"].includes(t) && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.soumis_cotisations !== false}
                     onChange={(e) => set("soumis_cotisations", e.target.checked)}
                     className="rounded border-gray-300 text-[#52a675]" data-testid="evt-soumis" />
              Soumis aux cotisations sociales
            </label>
          )}

          <F label="Libellé / commentaire">
            <Textarea rows={2} value={form.libelle || ""} onChange={(e) => set("libelle", e.target.value)}
                      placeholder="Apparaîtra sur le bulletin de salaire" data-testid="evt-libelle" />
          </F>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="evt-cancel">Annuler</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="evt-save">
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
